import { FullscreenOverlayContainer, Overlay, OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import {ActivatedRoute, Params, Router} from '@angular/router';
import { MultiFieldEditorContent } from '@app/order/models/multi-field-editor-content';
import { TermsAndConditionsDialogComponent } from '@app/shared/components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { CalculateRequest } from '@core/models/calculate-request';
import { CassError } from '@core/models/cass-error';
import { OrderContactDetails } from '@core/models/order-contact-details.model';
import { OrderUpdate } from '@core/models/order-update.model';
import { Order } from '@core/models/order.model';
import { Package } from '@core/models/package.model';
import { PlacementCategory } from '@core/models/placement-category.model';
import { Placement } from '@core/models/placement.model';
import { StyleElement } from '@core/models/style-element';
import { Style } from '@core/models/style.model';
import { AppConfigService } from '@core/services/app-config.service';
import { AuthService } from '@core/services/auth.service';
import { IssueDateService } from '@core/services/issue-date.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { Logger } from '@core/services/logger.service';
import { OrdersService } from '@core/services/orders.service';
import { PackageService } from '@core/services/package.service';
import { PlacementCategoryService } from '@core/services/placement-category.service';
import { StyleService } from '@core/services/style.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderOwner } from '@order/models/order-owner';
import { OrderViewModel } from '@order/models/order-view-model.model';
import { PersistentOrder } from '@order/models/persistent-order';
import { EditOrderConversionService } from '@order/services/edit-order-conversion.service';
import { PaymentGatewayCode, PaymentGatewayPaytrailService } from '@order/services/payment-gateway-paytrail.service';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { CassValidators } from '@shared/models/cass-validators';
import { ContactDetailsPersistentModel } from '@shared/models/contact-details-persistent-model';
import { MultiFieldContent } from '@shared/models/multi-field-content';
import { MultiFieldTextEditorCacheService } from '@shared/services/multi-field-text-editor-cache.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CalculatePriceRequestService } from '../services/calculate-price-request.service';
import { OrderHelperService } from '../services/order-helper.service';
import { OrderUpdateRequestService } from '../services/order-update-request.service';
import {OrderCreationNavigationState} from '@shared/models/order-creation-navigation-state';

enum OrderProperty {
	All,
	Placement,
	Package,
	Style,
	Issues,
	Content,
	CustomerNumber
}

enum TextEditorType {
	SIMPLE,
	CKEDTOR,
	MULTIFIELD
}

class DeferredCalcParams {
	render: boolean;
	timestamp: number;

	constructor(render: boolean, timestamp: number) {
		this.render = render;
		this.timestamp = timestamp;
	}
}

@Component({
	selector: 'cass-edit-order',
	templateUrl: './edit-order.component.html',
	styleUrls: ['./edit-order.component.css'],
	providers: [
		{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } },
		{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }
	]
})
export class EditOrderComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "EditOrderComponent";

	@ViewChild('editOrderStepper')
	private stepper!: MatStepper;

	orderVM: OrderViewModel = new OrderViewModel();

	readonly PaymentGatewayCode = PaymentGatewayCode; // exposes enum to template

	private _orderOwner: OrderOwner | undefined;

	constructor (
		private _router: Router,
		private logger: Logger,
		private route: ActivatedRoute,
		private location: LocationStrategy,
		private orderService: OrdersService,
		private placementCategoryService: PlacementCategoryService,
		private packageService: PackageService,
		private styleService: StyleService,
		private issuesService: IssueDateService,
		private conversionService: EditOrderConversionService,
		private paytrailService: PaymentGatewayPaytrailService,
		private translate: TranslateService,
		private multiFieldCacheService: MultiFieldTextEditorCacheService,
		private localStorageService: LocalStorageService,
		private userAlertService: UserAlertService,
		private overlay: Overlay,
		private titleService: Title,
		private calculateRequestService: CalculatePriceRequestService,
		private orderUpdateRequestService: OrderUpdateRequestService,
		private helperService: OrderHelperService,
		private dialog: MatDialog,
		private http: HttpClient,
		private sanitizer: DomSanitizer,
		public authService: AuthService,
		public appConfigService: AppConfigService
	) {

		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			if (nav.extras.state instanceof OrderCreationNavigationState || nav.extras.state["className"] === "OrderCreationNavigationState") {
				const state = nav.extras.state as OrderCreationNavigationState;
				this._orderOwner = state?.orderOwner;
			}
		}
	}

	isSelectPlacementCategoryCompleted: boolean = false;
	isSelectPlacementCompleted: boolean = false;
	isSelectPackageCompleted: boolean = false;
	isAdDetailsSectionCompleted: boolean = false;
	productNames: string[] | undefined = [];

	currentStep: number = -1;

	private subscription: Subscription = Subscription.EMPTY;
	private adDetailsConfirmedSubject = new Subject<void>();
	private customerDetailsCompletedSubject = new Subject<void>();

	private calcRenderSubject = new Subject<DeferredCalcParams>();

	calculatingOverlay$ = new BehaviorSubject<boolean>(false);
	loadingOrderOverlay$ = new BehaviorSubject<boolean>(false);
	loadingDataOverlay$ = new BehaviorSubject<boolean>(false);
	payingOverlay$ = new BehaviorSubject<boolean>(false);
	private fullscreenProgressOverlayRef?: OverlayRef;

	staticDataPlacementCategories?: PlacementCategory[];
	staticDataPlacements?: Placement[];
	staticDataPackages?: Package[];
	staticDataStyles?: Style[];
	staticDataIssueDates: Date[] = [];

	multiFieldInitialContent?: MultiFieldContent[] = undefined;
	initialContactDetails?: ContactDetailsPersistentModel = undefined;

	// Sometimes angular's binding to a length of an array fails with an exception; use a variable to prevent this
	issueDateCount: number = 0;

	paytrailLegacyEnabled = false;
	paytrail2022Enabled = false;

	ngOnInit(): void {
		this.logger.info(this.COMPONENT_NAME, "Edit order: starting...");
		this.translate.get("APP.TITLE").toPromise().then((title) => this.titleService.setTitle(title));

		this.initTermsAndConditionsCheck();

		this.paytrail2022Enabled = this.appConfigService.config?.enabledPaymentGateways?.includes(PaymentGatewayCode.PAYTRAIL_2022) ?? false;
		this.paytrailLegacyEnabled = this.appConfigService.config?.enabledPaymentGateways?.includes(PaymentGatewayCode.PAYTRAIL_LEGACY) ?? false;

		history.pushState(null, "Edit Order Stepper - initial state", window.location.href);
		this.location.onPopState(() => {
			if (this.stepper.selectedIndex > 0) {
				if (this.stepper.selectedIndex > 1) {
					history.pushState(null, `Edit Order Stepper ${this.stepper.selectedIndex}`, window.location.href);
				}

				this.stepper.previous();
			}
		});

		this.fullscreenProgressOverlayRef = this.overlay.create({
			positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this.loadingOrderOverlay$.pipe(distinctUntilChanged()).subscribe(show => {
			if (show) {
				const componentRef = this.fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
				if (componentRef) {
					this.translate.get("ORDER.LOADING_ORDER_MESSAGE").toPromise()
						.then((msg) => {
							componentRef.instance.displayString = msg;
						});
				}
			}
			else {
				this.fullscreenProgressOverlayRef?.detach();
			}
		});

		this.loadingDataOverlay$.pipe(distinctUntilChanged()).subscribe(show => {
			if (show) {
				const componentRef = this.fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
				if (componentRef) {
					this.translate.get("APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE").toPromise()
						.then((msg) => {
							componentRef.instance.displayString = msg;
						});
				}
			}
			else {
				this.fullscreenProgressOverlayRef?.detach();
			}
		});

		this.payingOverlay$.pipe(distinctUntilChanged()).subscribe(show => {
			if (show) {
				const componentRef = this.fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
				if (componentRef) {
					this.translate.get("ORDER.OPENING_PAYMENT_GATEWAY_MESSAGE").toPromise()
						.then((msg) => {
							componentRef.instance.displayString = msg;
						});
				}
			}
			else {
				this.fullscreenProgressOverlayRef?.detach();
			}
		});

		this.updatePricingAndPreviewMessage();
		this.multiFieldCacheService.reset();

		const adDetailseSubscription = this.adDetailsConfirmedSubject.pipe(debounceTime(200)).subscribe(() => {
			this.confirmAdDetailsHandler();
		})
		this.subscription = adDetailseSubscription;

		const customerDetailsSubscription = this.customerDetailsCompletedSubject.pipe(debounceTime(200)).subscribe(() => {
			this.customerDetailsCompletedHandler();
		})
		this.subscription.add(customerDetailsSubscription);

		const calcRenderSubscription = this.calcRenderSubject.pipe(debounceTime(1000)).subscribe((needsRender) => {
			this.deferredCalc(needsRender);
		})
		this.subscription.add(calcRenderSubscription);

		if (this.route.snapshot.queryParams["place"]) {
			this.InitOrderFromUrl(this.route.snapshot.queryParams);
		}
		else if (this.localStorageService.isOrderStored()) {
			this.InitOrderFromLocalStorage();
		}
		else {
			this.initNewOrder();
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private initNewOrder(): void {
		this.logger.info(this.COMPONENT_NAME, "Starting new order...");
		this.loadStaticDataPlacementCategories().catch(() => {
			this.userAlertService.errorByTranslationKey("ERRORS.CANNOT_RETRIEVE_PLACEMENT_CATEGORIES");
		});
	}

	private async InitOrderFromLocalStorage() {
		this.logger.info(this.COMPONENT_NAME, "Starting locally saved order...");
		try {
			const snapshot = this.localStorageService.loadOrder();
			if (!snapshot) {
				throw CassError.createErrorWithMessageKey("ERRORS.CANNOT_READ_ORDER_FROM_LOCAL_STORAGE");
			}

			const orderSnapshot = JSON.parse(snapshot) as PersistentOrder;

			if (!orderSnapshot.placementCategoryId) {
				throw CassError.createErrorWithMessageKey("ERRORS.ORDERS_PLACEMENT_CATEGORY_IS_NOT_SET");
			}

			if (!orderSnapshot.placementId) {
				throw CassError.createErrorWithMessageKey("ERRORS.ORDERS_PLACEMENT_IS_NOT_SET");
			}

			if (!orderSnapshot.packageId) {
				throw CassError.createErrorWithMessageKey("ERRORS.ORDERS_PACKAGE_IS_NOT_SET");
			}

			if (!orderSnapshot.styleId) {
				throw CassError.createErrorWithMessageKey("ERRORS.ORDERS_STYLE_IS_NOT_SET");
			}

			this.loadingOrderOverlay$.next(true);
			this.loadStaticDataPlacementCategories()
				.then(() => {
					const placementCategory = this.staticDataPlacementCategories?.find((c) => c.id === orderSnapshot.placementCategoryId);
					if (placementCategory) {
						return this.selectPlacementCategory(placementCategory);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PLACEMENT_CATEGORY_NOT_FOUND", { placementCategoryId: orderSnapshot.placementCategoryId }));
					}
				})
				.then(() => {
					const placement = this.staticDataPlacements?.find((p) => p.id === orderSnapshot.placementId);
					if (placement) {
						return this.selectPlacement(placement);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PLACEMENT_NOT_FOUND", { placementId: orderSnapshot.placementId }));
					}
				})
				.then(() => {
					const pckg = this.staticDataPackages?.find((p) => p.id === orderSnapshot.packageId);
					if (pckg) {
						return this.selectPackage(pckg);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PACKAGE_NOT_FOUND", { packageId: orderSnapshot.packageId }));
					}
				})
				.then(() => {
					const style = this.staticDataStyles?.find((s) => s.id === orderSnapshot.styleId);
					if (style) {
						return this.selectStyle(style, false);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.STYLE_NOT_FOUND", { styleId: orderSnapshot.styleId }));
					}
				})
				.then(() => {
					if (this.orderVM.contentVM.style && this.conversionService.isStyleMultifield(this.orderVM.contentVM.style)) {
						this.multiFieldInitialContent = orderSnapshot.multiFieldContents;
					}
					else {
						this.orderVM.contentVM.adText = orderSnapshot.adText;
					}

					return Promise.resolve();
				})
				.then(() => {
					if (orderSnapshot.issueDates && orderSnapshot.issueDates.length > 0) {
						const issueDatesAsStrings = this.staticDataIssueDates.map((issue) => this.conversionService.getIssueAsISOString(issue));
						const validIssues: string[] = [];
						orderSnapshot.issueDates.forEach((d) => {
							if (issueDatesAsStrings.find((id) => id === d)) {
								validIssues.push(d);
							}
						});
						const issueDates = validIssues.map((issue) => new Date(issue)) as readonly Date[];
						return this.selectIssues(issueDates, false);
					}
					else {
						return Promise.resolve();
					}
				})
				.catch((cassError) => {
					this.logger.error(this.COMPONENT_NAME, "Init of locally stored order failed", cassError);
					this.userAlertService.errorByCassError(cassError);
				})
				.finally(() => {
					if (orderSnapshot.contactDetails) {
						this.initialContactDetails = orderSnapshot.contactDetails;
						this.orderVM.subscriberNumber = orderSnapshot.subscriberNumber;
					}

					this.updateIndexDependingOnOrderData();
					this.loadingOrderOverlay$.next(false);
				});
		}
		catch (ex) {
			this.loadingOrderOverlay$.next(false);
			this.logger.error(this.COMPONENT_NAME, "InitLocallyStoredOrder(): caught exception", ex);
			if (ex instanceof CassError) {
				this.userAlertService.errorByCassError(ex);
			}
			else {
				this.userAlertService.errorByTranslationKey("ERRORS.UNKNOWN_ERROR");
			}
		}
	}

	private InitOrderFromUrl(params: Params) {
		this.logger.info(this.COMPONENT_NAME, "Starting InitOrderFromUrl...");

		try {
			const placementId = params["place"] as string;
			const packageId = params["pack"] as string;
			const styleId = params["style"] as string;
			const dates = params["dates"] as string;

			this.loadingOrderOverlay$.next(true);

			this.loadStaticDataPlacementCategories()
				.then(() => {
					if (!placementId) {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PLACEMENT_IS_NOT_PROVIDED"));
					}

					// find appropriate category by placement id
					const placementCategory = this.staticDataPlacementCategories?.find((c) => c.placements?.some(p => p.id === placementId));
					this.logger.debug(this.COMPONENT_NAME, `InitOrderFromUrl(): found a placement category id="${placementCategory?.id}" for placementId="${placementId}"`)
					if (placementCategory) {
						return this.selectPlacementCategory(placementCategory);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.CANNOT_FIND_CATEGORY_FOR_PLACEMENT", { placementId: placementId }));
					}
				})
				.then(() => {
					const placement = this.staticDataPlacements?.find((p) => p.id === placementId);
					if (placement) {
						return this.selectPlacement(placement);
					}
					else {
						// unrealistic error; the placement category was just found for this placement on the previous step
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PLACEMENT_NOT_FOUND", { placementId: placementId }));
					}
				})
				.then(() => {
					if (!packageId) {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PACKAGE_IS_NOT_PROVIDED"));
					}

					const pckg = this.staticDataPackages?.find((p) => p.id === packageId);
					if (pckg) {
						return this.selectPackage(pckg);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.PACKAGE_NOT_FOUND", { packageId: packageId }));
					}
				})
				.then(() => {
					// style is optional
					let style: Style | undefined = undefined;
					if (styleId) {
						style = this.staticDataStyles?.find((s) => s.id === styleId);
					}

					if (!style) {
						style = this.getDefaultStyle();
					}

					if (style) {
						return this.selectStyle(style, false);
					}
					else {
						return Promise.reject(CassError.createErrorWithMessageKey("ERRORS.STYLES_ARE_NOT_DEFINED_FOR_PACKAGE_AND_PLACEMENT"));
					}
				})
				.then(() => {
					// dates are optional
					const validIssues: Date[] = [];
					if (dates) {
						const dateStrings: string[] = dates.toString().split(",");
						for (let i = 0; i < dateStrings.length; ++i) {
							const dateString = dateStrings[i];
							const issueDate = this.helperService.convertUrlStringDateToDate(dateString);
							if (issueDate && this.helperService.doesDateArrayContainDate(this.staticDataIssueDates, issueDate)) {
								validIssues.push(issueDate);
							}
						}
					}

					if (validIssues.length > 0) {
						return this.selectIssues(validIssues, false);
					}
					else {
						const issueDate = this.getDefaultIssueDate();
						if (issueDate) {
							return this.selectIssues([issueDate], false);
						}
						else {
							return Promise.resolve();
						}
					}
				})
				.catch((cassError) => {
					this.logger.error(this.COMPONENT_NAME, "InitOrderFromUrl(): init failed", cassError);
					this.userAlertService.errorByCassError(cassError);
				})
				.finally(() => {
					this.updateIndexDependingOnOrderData();
					this.loadingOrderOverlay$.next(false);
				});
		}
		catch (ex) {
			this.loadingOrderOverlay$.next(false);
			this.logger.error(this.COMPONENT_NAME, "InitOrderFromUrl(): caught exception", ex);
			if (ex instanceof CassError) {
				this.userAlertService.errorByCassError(ex);
			}
			else {
				this.userAlertService.errorByTranslationKey("ERRORS.UNKNOWN_ERROR");
			}
		}
	}

	private updateIndexDependingOnOrderData() {
		let index = 0;
		if (this.isSelectPlacementCategoryCompleted) {
			index = 1;
		}
		if (this.isSelectPlacementCompleted) {
			index = 2;
		}
		if (this.isSelectPackageCompleted) {
			index = 3;
		}

		if (index > 0) {
			this.logger.info(this.COMPONENT_NAME, `InitUrlOrder(), tab index=${index}`);
			setTimeout(() => {
				this.currentStep = index;
				this.orderPropertyUpdated(OrderProperty.All);
			}, 0);
		}
	}

	private loadStaticDataPlacementCategories(): Promise<void> {
		const promise = new Promise<void>((resolve, reject) => {
			this.placementCategoryService.getPlacementCategories().subscribe(
				(categories) => {
					this.staticDataPlacementCategories = categories;
					if (!this.staticDataPlacementCategories || this.staticDataPlacementCategories.length < 1) {
						reject(CassError.createErrorWithMessageKey("ERRORS.PLACEMENT_CATEGORIES_ARE_NOT_DEFINED"));
					}

					for (const category of this.staticDataPlacementCategories) {
						if (!category.placements || category.placements.length < 1) {
							this.logger.error(this.COMPONENT_NAME, `Configuration: Placements are not assigned for placementCategoryId="${category.id}"`);
						}
					}

					resolve();
				},
				(error) => {
					this.logger.error(this.COMPONENT_NAME, "Failed to retrieve placement categories", error);
					reject(error);
				});
		});
		return promise;
	}

	private loadStaticDataPackages(placementId: string): Promise<void> {
		const promise = new Promise<void>((resolve, reject) => {
			this.packageService.getPackagesByPlacement(placementId).subscribe(
				(packages) => {
					this.staticDataPackages = packages;
					if (!this.staticDataPackages || this.staticDataPackages.length === 0) {
						this.logger.error(this.COMPONENT_NAME, `Configuration: Packages are not assigned for placementId="${placementId}"`);
					}

					for (const pckg of this.staticDataPackages) {
						if (!pckg.products || pckg.products.length < 1) {
							this.logger.error(this.COMPONENT_NAME, `Configuration: Products are not assigned for packageId="${pckg.id}"`);
						}
					}

					resolve();
				},
				(error) => {
					this.logger.error(this.COMPONENT_NAME, `Failed to retrieve packages for a placement id="${placementId}"`, error);
					reject(error);
				});
		});
		return promise;
	}

	private loadStaticDataStyles(placementId?: string, packageId?: string): Promise<void> {
		this.staticDataStyles = [];
		const promise = new Promise<void>((resolve, reject) => {
			if (!placementId || !packageId) {
				resolve();
			}
			else {
				this.styleService.getStylesForPackageAndPlacement(packageId, placementId).toPromise().then(
					(styles) => {
						this.staticDataStyles = styles;
						if (!this.staticDataStyles || this.staticDataStyles.length === 0) {
							this.logger.error(this.COMPONENT_NAME, `Configuration: Styles are not assigned for packageId="${packageId}", placementId="${placementId}"`);
						}
						resolve();
					},
					(error) => {
						reject(error);
					});
			}
		});
		return promise;
	}

	private loadStaticDataIssueDates(placementId?: string, packageId?: string): Promise<void> {
		this.staticDataIssueDates = [];
		const promise = new Promise<void>((resolve, reject) => {
			if (!placementId || !packageId) {
				return resolve();
			}
			else {
				this.issuesService.getDatesForPackageAndPlacement(packageId, placementId).subscribe(
					(issues) => {
						this.staticDataIssueDates = issues;
						if (!this.staticDataIssueDates || this.staticDataIssueDates.length === 0) {
							this.logger.error(this.COMPONENT_NAME, `Configuration: Issues are not assigned for packageId="${packageId}", placementId="${placementId}"`);
						}
						resolve();
					},
					(error) => {
						this.logger.error(this.COMPONENT_NAME, `Failed to retrieve issue dates for packageId="${packageId}" and placementId="${placementId}"`, error);
						reject(error);
					});
			}
		});
		return promise;
	}

	uiSelectPlacementCategory(placementCategory: PlacementCategory): Promise<void> {
		// Promise is used only for testing purposes.
		const promise = new Promise<void>((resolve, reject) => {
			this.selectPlacementCategory(placementCategory).then(
				() => {
					if (!this.staticDataPlacements || this.staticDataPlacements.length < 1) {
						this.userAlertService.errorByTranslationKey("ERRORS.PLACEMENTS_ARE_NOT_DEFINED");
						reject();
					}
					else {
						if (this.stepper.selected) {
							this.stepper.selected.completed = true;
						}
						this.stepper.next();
						resolve();
					}
				},
				(error: CassError) => {
					this.userAlertService.errorByCassError(error);
					reject();
				}
			)
		});
		return promise;
	}

	private selectPlacementCategory(placementCategory: PlacementCategory): Promise<void> {
		if (!this.orderVM.adVM.placementCategory || this.orderVM.adVM.placementCategory.id !== placementCategory.id) {
			this.orderVM.adVM.placementCategory = placementCategory;
			this.logger.info(this.COMPONENT_NAME, `Selected placement category id="${placementCategory?.id}"`);
			this.staticDataPlacements = placementCategory.placements;
			this.isSelectPlacementCategoryCompleted = true;
		}

		return Promise.resolve();
	}

	uiSelectPlacement(placement: Placement): Promise<void> {
		// Promise is used only for testing purposes.
		const promise = new Promise<void>((resolve, reject) => {
			this.loadingDataOverlay$.next(true);
			this.selectPlacement(placement).then(
				() => {
					this.loadingDataOverlay$.next(false);
					if (!this.staticDataPackages || this.staticDataPackages.length < 1) {
						this.userAlertService.errorByTranslationKey("ERRORS.PACKAGES_ARE_NOT_DEFINED_FOR_PLACEMENT");
						reject();
					}
					else {
						if (this.stepper.selected) {
							this.stepper.selected.completed = true;
						}
						this.stepper.next();

						if (this.staticDataPackages.length === 1) {
							// automatically select the package/publication when there is only one in the list
							this.uiSelectPackage(this.staticDataPackages[0]).finally(() => {
								// resolve the current promise regardless of the package selection result
								resolve();
							});
						}
						else {
							resolve();
						}
					}
				},
				(error: CassError) => {
					this.loadingDataOverlay$.next(false);
					this.userAlertService.errorByCassError(error);
					reject();
				}
			);
		});
		return promise;
	}

	private selectPlacement(placement: Placement): Promise<void> {
		this.orderVM.adVM.placement = placement;
		this.orderVM.adVM.package = undefined;
		this.logger.info(this.COMPONENT_NAME, `Selected placement id="${placement?.id}"`);

		// If changing to a placement that is not eligible for subscriber discount, clear the customer number
		if (!placement.subscriberDiscountEligible) {
			this.orderVM.subscriberNumber = undefined;
		}

		this.isSelectPlacementCompleted = true;

		return this.loadStaticDataPackages(placement.id);
	}

	private isSelectPackageWebCallInProgress = false;
	uiSelectPackage(pckg: Package): Promise<void> {
		if (this.isSelectPackageWebCallInProgress) {
			return Promise.resolve();
		}

		// Promise is used only for testing purposes.
		const promise = new Promise<void>((resolve, reject) => {
			this.isSelectPackageWebCallInProgress = true;
			this.loadingDataOverlay$.next(true);
			this.selectPackage(pckg).then(
				() => {
					this.isSelectPackageWebCallInProgress = false;
					this.loadingDataOverlay$.next(false);
					if (!pckg.products || pckg.products.length < 1) {
						this.userAlertService.errorByTranslationKey("ERRORS.PRODUCTS_ARE_NOT_DEFINED_FOR_PACKAGE");
						reject();
					}
					else if (!this.staticDataStyles || this.staticDataStyles.length < 1) {
						this.userAlertService.errorByTranslationKey("ERRORS.STYLES_ARE_NOT_DEFINED_FOR_PACKAGE_AND_PLACEMENT");
						reject();
					}
					else if (!this.staticDataIssueDates || this.staticDataIssueDates.length < 1) {
						this.userAlertService.errorByTranslationKey("ERRORS.ISSUES_ARE_NOT_DEFINED_FOR_PACKAGE_AND_PLACEMENT");
						reject();
					}
					else {
						this.orderPropertyUpdated(OrderProperty.Package);
						this.selectPackageAndPlacementDependentDefaults();
						if (this.stepper.selected) {
							this.stepper.selected.completed = true;
						}
						this.stepper.next();
						resolve();
					}
				},
				(error: CassError) => {
					this.isSelectPackageWebCallInProgress = false;
					this.loadingDataOverlay$.next(false);
					this.userAlertService.errorByCassError(error);
					reject();
				}
			);
		});
		return promise;
	}

	private selectPackage(pckg: Package): Promise<void> {
		const promise = new Promise<void>((resolve, reject) => {
			this.orderVM.adVM.package = pckg;
			this.logger.info(this.COMPONENT_NAME, `Selected package id="${pckg?.id}"`);
			this.setProductNames(pckg);
			Promise.all([
				this.loadStaticDataStyles(this.orderVM.adVM.placement?.id, this.orderVM.adVM.package?.id),
				this.loadStaticDataIssueDates(this.orderVM.adVM.placement?.id, this.orderVM.adVM.package?.id)
			]).then(() => {
				this.isSelectPackageCompleted = true;
				resolve();
			}).catch(error => {
				reject(error);
			});
		});
		return promise;
	}

	selectStyle(style: Style, updateUI = true): Promise<void> {
		this.logger.debug(this.COMPONENT_NAME, `Selecting style id="${style?.id}"`);
		this.orderVM.contentVM.style = style;

		this.acivateTextEditor(style);

		if (updateUI) {
			this.orderPropertyUpdated(OrderProperty.Style);
		}

		this.logger.info(this.COMPONENT_NAME, `Selected style id="${style?.id}"`);

		return Promise.resolve();
	}

	selectIssues(issues: readonly Date[], updateUI = true): Promise<void> {
		this.orderVM.adVM.issueDates = issues;

		this.issueDateCount = issues?.length || 0;

		if (updateUI) {
			if (this.orderVM.adVM.issueDates.length === 0) {
				// clear pricing
				this.conversionService.resetPricing(this.orderVM);
				this.logger.info(this.COMPONENT_NAME, "Issues cleared");
			}
			this.orderPropertyUpdated(OrderProperty.Issues);
		}

		if (issues.length > 0) {
			try {
				const datesAsStrings = this.orderVM.adVM.issueDates.map(d => this.conversionService.getIssueAsISOString(d));
				const allDates = datesAsStrings.join(", ");
				this.logger.info(this.COMPONENT_NAME, "Selected issues: " + allDates);
			}
			catch (error) {
				this.logger.error(this.COMPONENT_NAME, "Caught exception while logging selected issues", error);
			}
		}

		return Promise.resolve();
	}

	private setProductNames(pckg: Package): void {
		this.productNames = [];

		if (Array.isArray(pckg.products)) {
			pckg.products.forEach((prod) => {
				this.productNames?.push(prod.label);
			});
		}
	}

	private selectPackageAndPlacementDependentDefaults(): void {

		if (this.orderVM.contentVM.style) {
			const styleId = this.orderVM.contentVM.style.id;
			if (Array.isArray(this.staticDataStyles) && this.staticDataStyles.length > 0) {
				if (!this.staticDataStyles.find(s => s.id === styleId)) {
					// remove selected style
					this.orderVM.contentVM.style = undefined;
				}
			}
		}

		if (!this.orderVM.contentVM.style) {
			const style = this.getDefaultStyle();
			if (style) {
				this.selectStyle(style);
			}
		}

		// [CASS-35] Disable automatic default publish date selection
		// TODO: make this configurable - some sites may want date to be defaulted
		// if (this.orderVM.adVM.issueDates.length === 0) {
		// 	const issueDate = this.getDefaultIssueDate();
		// 	if (issueDate) {
		// 		this.selectIssues([issueDate]);
		// 	}
		// }

		// Check for validity of existing date selections
		if (this.orderVM.adVM.issueDates && this.orderVM.adVM.issueDates.length > 0) {
			const validIssues: Date[] = [];
			this.orderVM.adVM.issueDates.forEach(issueDate => {
				if (this.helperService.doesDateArrayContainDate(this.staticDataIssueDates, issueDate)) {
					validIssues.push(issueDate);
				}
			});
			this.selectIssues(validIssues, false);
		}

	}

	private getDefaultStyle(): Style | undefined {
		if (!this.orderVM.contentVM.style && Array.isArray(this.staticDataStyles) && this.staticDataStyles.length > 0) {
			return this.staticDataStyles[0];
		}
		return undefined;
	}

	private getDefaultIssueDate(): Date | undefined {
		if (this.staticDataIssueDates.length > 0) {
			return this.staticDataIssueDates[0];
		}
		return undefined;
	}

	textEditorType?: TextEditorType;
	TextEditorType = TextEditorType; // make the enum available for use in the template
	multiFieldStyleElements: StyleElement[] | undefined = [];

	private acivateTextEditor(style: Style): void {
		if (style.elements && style.elements.length > 0) {
			this.multiFieldStyleElements = style.elements;
			this.textEditorType = TextEditorType.MULTIFIELD;
		}
		else {
			this.multiFieldStyleElements = undefined;
			this.textEditorType = TextEditorType.CKEDTOR;
		}
	}

	ckEditorContentChanged(editorContent: string | undefined) {
		this.orderVM.contentVM.adText = editorContent;
		this.orderPropertyUpdated(OrderProperty.Content);
	}

	multiFieldContentChanged(multiFieldContent: MultiFieldEditorContent): void {
		this.orderVM.contentVM.multiFieldEditorContent = multiFieldContent;

		this.orderPropertyUpdated(OrderProperty.Content);
	}

	pricingMessage?: string;
	pricingError: boolean = false;
	adPreviewMessage?: string;
	adPreviewError: boolean = false;

	private updateConfirmAndPayStatus() {
		this.isAdDetailsSectionCompleted = false;

		if (this.orderVM.isCalculated && this.orderVM.adVM.issueDates && this.orderVM.adVM.issueDates.length > 0 && this.orderVM.contentVM.style) {
			if (this.conversionService.isStyleMultifield(this.orderVM.contentVM.style)) {
				// Multi-field text style
				if (this.orderVM.contentVM.multiFieldEditorContent?.isComplete) {
					this.isAdDetailsSectionCompleted = true;
				}
			}
			else {
				// HTML Editor style
				if (this.orderVM?.contentVM?.adText) {
					this.isAdDetailsSectionCompleted = true;
				}
			}
		}
	}

	confirmAdDetails(): void {
		this.logger.debug(this.COMPONENT_NAME, "Confirming ad details...");
		this.adDetailsConfirmedSubject.next();
	}

	adDetailsConfirmed = false;
	private confirmAdDetailsHandler(): void {
		this.logger.info(this.COMPONENT_NAME, "Ad details confirmed, activating the Confirm Customer Details step");
		this.adDetailsConfirmed = true;
		setTimeout(() => {
			this.stepper.next();
		}, 0);
	}

	safeHtmlTermsAndConditions?: SafeHtml;

	// Note: validator is added in the initialiser below
	agreeToTermsAndConditions: FormControl = new FormControl(false);

	private initTermsAndConditionsCheck(): void {
		this.http.get('./branding/html/terms-and-conditions-dialog.html', { responseType: 'text' })
			.toPromise()
			.then(html => {
				if (html) {
					this.safeHtmlTermsAndConditions = this.sanitizer.bypassSecurityTrustHtml(html);
				}
				this.agreeToTermsAndConditions.setValidators(CassValidators.cassRequiredTrue("FORMS_FIELDS_VALIDATION_ERRORS.TERMS_AND_CONDITIONS_NOT_ACCEPTED"));
			})
			.catch(() => {});
	}

	customerDetailsForm = new FormGroup({
		agreeToTermsAndConditions: this.agreeToTermsAndConditions
	});

	openTermsAndConditionsDialog(): void {
		this.dialog.open(TermsAndConditionsDialogComponent, { data: { safeHtmlContent: this.safeHtmlTermsAndConditions } });
	}

	customerDetailsConfirmed = false;
	customerDetailsCompleted(): void {
		this.logger.debug(this.COMPONENT_NAME, "Confirming customer details...");
		this.customerDetailsConfirmed = true;
		this.customerDetailsCompletedSubject.next();
	}

	private customerDetailsCompletedHandler(): void {
		this.logger.info(this.COMPONENT_NAME, "Customer details confirmed, activating the Payment step");
		setTimeout(() => {
			this.stepper.next();
		}, 0);
	}

	discountAppliedMessage: string = "";
	discountFailedMessage: string = "";

	customerNumberChanged(customerNumber: string | undefined): void {
		// reset the messages here so Angular can detect the change when the calc result is received
		this.discountFailedMessage = "";
		this.discountAppliedMessage = "";

		this.orderVM.subscriberNumber = customerNumber;
		this.orderPropertyUpdated(OrderProperty.CustomerNumber);

		if (customerNumber) {
			this.logger.info(this.COMPONENT_NAME, `User applied Customer Number "${customerNumber}"`);
		}
		else {
			this.logger.info(this.COMPONENT_NAME, "User deselected the Customer Number");
			this.discountAppliedMessage = "";
		}
	}

	private activeCalcRequestTimestamp?: number;
	private activeCalcNeedsRender = false;

	private orderPropertyUpdated(entity: OrderProperty) {
		if (entity !== OrderProperty.CustomerNumber) {
			this.adDetailsConfirmed = false;
		}

		this.customerDetailsConfirmed = false;
		this.agreeToTermsAndConditions.setValue(false);

		this.conversionService.resetPricing(this.orderVM);

		this.updatePricingAndPreviewMessage();
		this.updateConfirmAndPayStatus();

		let needsRender = false;

		switch (entity) {
			//case OrderProperty.Package:
			//case OrderProperty.Placement:
			//	this.needsCalc = true;
			//	break;
			case OrderProperty.Issues:
				if (!this.orderVM.adVM.issueDates || this.orderVM.adVM.issueDates.length === 0) {
					// user de-selected all issues, no need to calc/render
					return;
				}
				break;
			//	if (this.orderVM.adVM.issueDates?.length > 0) {
			//		this.needsCalc = true;
			//	}
			//	break;
			case OrderProperty.All:
			case OrderProperty.Style:
				// this.needsRender = true;
				needsRender = true;
				this.conversionService.resetCache(this.orderVM);
				break;

			case OrderProperty.Content:
				// this.needsRender = true;
				needsRender = true;
				//this.conversionService.resetPricing(this.orderVM);
				this.conversionService.resetCache(this.orderVM);
				break;
		}


		if (this.conversionService.canCalculatePrice(this.orderVM)) {
			if (!needsRender && !this.orderVM.contentVM.preview) {
				// Covers scenario when user changed the ad text and deselected all the issues; then after some time re-selected some.
				// Selection of an issue date does not require rendering; but in this case the rendering is required.
				needsRender = true;
			}

			// During the calculatePrice call-out time, the ad data can change.
			// In this case current call-out request/response timestamp will be different from this.activeCalcRequestTimestamp and the response is ignored, so the ad data will be updated by the last response.
			// The this.activeCalcRequestTimestamp contains the timestamp of the most recent event before any async processing took place, so the deferredCalc method can ignore obsolete responses.
			// The only special case is the render flag. If any of the requests required rendering, all the subsequent requests must require rendering as well
			// to make sure the rendering data is not ignored and not lost if the last event does not require rendering.
			// This is only relevant when a few events are issued during the call-out time, so the processed calculatePrice response resets this.activeCalcNeedsRender flag.
			this.activeCalcRequestTimestamp = Date.now();
			this.activeCalcNeedsRender = this.activeCalcNeedsRender || needsRender;

			this.calcRenderSubject.next(new DeferredCalcParams(this.activeCalcNeedsRender, this.activeCalcRequestTimestamp));
		}
		else {
			this.calculatingOverlay$.next(false);

			if (this.conversionService.isContentEmpty(this.orderVM)) {
				this.logger.debug(this.COMPONENT_NAME, "calc/render: the content is empty, resetting preview...");
				this.activeCalcRequestTimestamp = Date.now();
				this.orderVM.contentVM.preview = undefined;
			}
		}
	}

	private deferredCalc(params: DeferredCalcParams) {
		if (!this.conversionService.canCalculatePrice(this.orderVM)) {
			// user could change the order during the waiting period
			return;
		}

		const calculateRequest: CalculateRequest = this.calculateRequestService.createRequest(this.orderVM, params.render);

		this.calculatingOverlay$.next(true);

		const calloutStart = Date.now();
		this.orderService.calculatePrice(calculateRequest).subscribe(
			(order) => {
				const calloutEnd = Date.now();
				this.logger.debug(this.COMPONENT_NAME, `calculatePrice web-call succeeded, CalloutDuration=${calloutEnd - calloutStart}msec`);

				// process the response only if no new requests were issued during the call-out time
				if (params.timestamp === this.activeCalcRequestTimestamp) {
					this.calculatingOverlay$.next(false);
					this.conversionService.updateOrderModel(this.orderVM, order, params.render);
					this.orderVM.isCalculated = true;
					this.activeCalcNeedsRender = false;
					this.updateDiscountAppliedStatus(calculateRequest, order);
					this.logger.info(this.COMPONENT_NAME, "Calc/render succeeded");
				}
				//else {
				//	console.log("Content: response ignored due to obsolete timestamp");
				//}
			},
			(error) => {
				this.calculatingOverlay$.next(false);
				this.orderVM.isCalculated = false;
				const e = error as CassError;
				if (e.isUnexpected) {
					// if the error was unexpected (e.g. 500 server error), then show a popup alert
					this.userAlertService.errorByCassError(e);
				}
				else if (e && e.errorMessageKey) {
					if (e.isDiscountApplicationError && e.errorMessageKey) {
						// discount-realted errors are shown in its own section
						this.discountFailedMessage = this.translate.instant(e.errorMessageKey, e.errorInterpolateParams);
					}
					else {
						this.pricingMessage = this.translate.instant(e.errorMessageKey, e.errorInterpolateParams);
					}
				}
				else if (e.errorMessage) {
					this.pricingMessage = e.errorMessage;
				}
				this.adPreviewError = this.pricingError = true;
				this.adPreviewMessage = this.pricingMessage; // these errors are shown in both panels
				this.logger.error(this.COMPONENT_NAME, "Calc/render has failed", error);
			},
			() => {
				this.updateConfirmAndPayStatus();
			});
	}

	private async updatePricingAndPreviewMessage() {
		this.adPreviewMessage = "";
		this.pricingMessage = "";
		this.adPreviewError = this.pricingError = false; // these are not deemed "errors"
		if (!this.conversionService.areMandatoryFieldsSet(this.orderVM)) {
			if (this.conversionService.hasIssueDates(this.orderVM)) {
				this.adPreviewMessage = this.pricingMessage = await this.translate.get("ORDER.PRICE_BREAKDOWN.FILL_IN_REQUIRED_FIELDS_MESSAGE").toPromise();
			}
			else {
				this.pricingMessage = await this.translate.get("ORDER.PRICE_BREAKDOWN.SELECT_DATES_MESSAGE").toPromise();
			}
		}
	}

	private async updateDiscountAppliedStatus(calculateRequest: CalculateRequest, order: Order) {
		if (calculateRequest && calculateRequest.subscriberNumber && order && order.subscriberDiscountApplied) {
			this.discountAppliedMessage = await this.translate.get("ORDER.PRICE_BREAKDOWN.SUBSCRIBER_DISCOUNT_APPLIED").toPromise();
		}
		else {
			this.discountAppliedMessage = "";
		}
	}

	isProceedToPaymentActivated = false;
	proceedToPayment(gateway: PaymentGatewayCode): void {
		this.logger.info(this.COMPONENT_NAME, "User agreed to Proceed to Payment. Saving the order and activating the payment gateway...");

		if (this.isProceedToPaymentActivated) {
			this.logger.debug(this.COMPONENT_NAME, "Proceeding to Payment: already active; leaving...");
			return;
		}

		this.isProceedToPaymentActivated = true;

		const orderContactDetails: OrderContactDetails = this.conversionService.createOrderContactDetails(this.customerDetailsForm);
		const orderUpdate: OrderUpdate = this.orderUpdateRequestService.createRequest(this.orderVM, orderContactDetails);

		// TBD: create account if requested to do so

		this.payingOverlay$.next(true);
		this.logger.debug(this.COMPONENT_NAME, "Payment step: saving the order...");
		this.orderService.createOrUpdateOrder(orderUpdate).subscribe(
			(response) => {
				let order = response.order;
				if (!order || !order.orderHeader || !order.orderHeader.id) {
					this.logger.error(this.COMPONENT_NAME, "Received createOrUpdateOrder response is invalid. Cannot proceed with payment.");
					this.isProceedToPaymentActivated = false;
					this.payingOverlay$.next(false);
					this.userAlertService.errorByTranslationKey("ERRORS.CANNOT_SAVE_ORDER");
					return;
				}

				this.logger.info(this.COMPONENT_NAME, `Payment step: order saved, orderId="${order.orderHeader.id}"`);

				this.storeOrderInLocalStorage(orderContactDetails);

				// update the order id as the payment system must know it to make a payment
				this.logger.debug(this.COMPONENT_NAME, `Payment step: requesting the payment form and acivating the payment gateway '${gateway}'...`);
				this.paytrailService.proceedToPayment(order.orderHeader.id, gateway).then(
					() => {
						this.logger.info(this.COMPONENT_NAME, `Payment step: payment form received for orderId="${order?.orderHeader?.orderNumber}"`);
						this.logger.uploadLogs();
					},
					(error) => {
						this.payingOverlay$.next(false);
						this.logger.error(this.COMPONENT_NAME, "Payment form failed", error);
						this.userAlertService.errorByTranslationKey("ERRORS.CANNOT_RETRIEVE_PAYMENT_INFO");
						this.isProceedToPaymentActivated = false;
					});
			},
			(error) => {
				this.payingOverlay$.next(false);
				this.logger.error(this.COMPONENT_NAME, "Cannot save the order", error);
				this.userAlertService.errorByTranslationKey("ERRORS.CANNOT_SAVE_ORDER");
				this.isProceedToPaymentActivated = false;
			});
	}

	private storeOrderInLocalStorage(orderContactDetails?: OrderContactDetails): void {
		// move this to an appopriate place

		const persistentOrder = new PersistentOrder();
		persistentOrder.placementCategoryId = this.orderVM.adVM?.placementCategory?.id;
		persistentOrder.placementId = this.orderVM.adVM?.placement?.id;
		persistentOrder.packageId = this.orderVM.adVM?.package?.id;
		persistentOrder.styleId = this.orderVM.contentVM?.style?.id;
		persistentOrder.subscriberNumber = this.orderVM.subscriberNumber;

		if (this.orderVM.adVM?.issueDates?.length) {
			persistentOrder.issueDates = this.orderVM.adVM.issueDates.map(issueDate => this.conversionService.getIssueAsISOString(issueDate));
		}

		persistentOrder.adText = this.orderVM.contentVM.adText;

		persistentOrder.multiFieldContents = [];

		this.orderVM.contentVM.multiFieldEditorContent?.contents.forEach((multifield) => {
			if (multifield.key) {
				const cachedMultiField = this.multiFieldCacheService.getItem(multifield.key);
				if (cachedMultiField) {
					const persistentMultifield = new MultiFieldContent(multifield.key);
					if (cachedMultiField.textContent) {
						persistentMultifield.textContent = cachedMultiField.textContent;
					}
					if (cachedMultiField.imageContent) {
						persistentMultifield.fileName = cachedMultiField.fileName;
						persistentMultifield.imageContent = cachedMultiField.imageContent;
						persistentMultifield.imageWidth = cachedMultiField.imageWidth;
						persistentMultifield.imageHeight = cachedMultiField.imageHeight;
						persistentMultifield.imageSource = cachedMultiField.imageSource;
					}
					persistentOrder.multiFieldContents?.push(persistentMultifield);
				}
			}
		});

		if (orderContactDetails) {
			persistentOrder.contactDetails = new ContactDetailsPersistentModel();
			persistentOrder.contactDetails.email = orderContactDetails.email;
			persistentOrder.contactDetails.firstName = orderContactDetails.firstName;
			persistentOrder.contactDetails.lastName = orderContactDetails.lastName;
			persistentOrder.contactDetails.address1 = orderContactDetails.address1;
			persistentOrder.contactDetails.address2 = orderContactDetails.address2;
			persistentOrder.contactDetails.city = orderContactDetails.city;
			persistentOrder.contactDetails.state = orderContactDetails.state;
			persistentOrder.contactDetails.country = orderContactDetails.country;
			persistentOrder.contactDetails.zipcode = orderContactDetails.zipcode;
			persistentOrder.contactDetails.phone = orderContactDetails.phone;
		}

		try {
			// in case the storage is not allowed, an exception is expected, and the app should work without saved order,
			// i.e. in case the payment is cancelled, the user starts from scratch
			const snapshot = JSON.stringify(persistentOrder);
			this.localStorageService.saveOrder(snapshot);
		}
		catch (error) {
			this.logger.error(this.COMPONENT_NAME, `Caught exception when converting/saving the order in the local storage: ${error}`);
		}
	}
}
